import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/common/Header';
import Loading from './components/common/Loading';
import AICoachWidget from './components/coach/AICoachWidget';

// Import all pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import Dashboard from './pages/Dashboard';
import RoadmapPage from './pages/RoadmapPage';
import LessonPage from './pages/LessonPage';
import AssessmentPage from './pages/AssessmentPage';
import ProjectPage from './pages/ProjectPage';
import PortfolioPage from './pages/PortfolioPage';
import SettingsPage from './pages/SettingsPage';
import RoleAssessmentPage from './pages/RoleAssessmentPage';

// ✅ COMPLETE Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <Loading text="Initializing..." />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// ✅ COMPLETE Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <Loading text="Loading..." />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// ✅ COMPLETE Layout Component for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>{children}</main>
      <AICoachWidget />
    </div>
  );
};

// ✅ COMPLETE Main App Component
function AppContent() {
  const { isLoading } = useAuth();
  
  if (isLoading) {
    return <Loading text="Initializing Intelli-learn..." />;
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          
          {/* Onboarding Route (authenticated but no header) */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

          <Route path="/role-assessment" element={<ProtectedRoute><RoleAssessmentPage /></ProtectedRoute>} />
          
          {/* Protected Routes with Layout */}
          <Route path="/dashboard" element={<ProtectedRoute><AuthenticatedLayout><Dashboard /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/roadmap" element={<ProtectedRoute><AuthenticatedLayout><RoadmapPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/lesson/:moduleId" element={<ProtectedRoute><AuthenticatedLayout><LessonPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/assessment/:moduleId" element={<ProtectedRoute><AuthenticatedLayout><AssessmentPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><AuthenticatedLayout><ProjectPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/portfolio" element={<ProtectedRoute><AuthenticatedLayout><PortfolioPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AuthenticatedLayout><SettingsPage /></AuthenticatedLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><AuthenticatedLayout><SettingsPage /></AuthenticatedLayout></ProtectedRoute>} />
          
          {/* Catch all route - redirect to dashboard if authenticated, otherwise to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Toast Notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              theme: {
                primary: '#10B981',
                secondary: '#ffffff',
              },
            },
            error: {
              duration: 4000,
              theme: {
                primary: '#EF4444',
                secondary: '#ffffff',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
